import { useState } from 'react';
import { Box, Tab, Tabs, useMediaQuery, useTheme, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useBankAccounts } from '../../hooks/useBankAccounts';
import AccountsOverview from './components/AccountsOverview';
import MovementsList from './components/MovementsList';
import MovementDialog from './components/MovementDialog';
import { TableTitleBox, CustomButton, LoadingSkeleton } from '../../common/components';
import { formatBankName } from './utils/formatters';

/**
 * Componente principal de Bank Movements
 */
const BankMovements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const { data: bankAccounts = [], isLoading: accountsLoading } = useBankAccounts();

  if (accountsLoading) {
    return <LoadingSkeleton />;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSelectedAccount(null);
    }
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setActiveTab(1);
  };

  const handleCreateMovement = () => {
    setSelectedMovement(null);
    setEditMode(false);
    setMovementDialogOpen(true);
  };

  const handleEditMovement = (movement) => {
    setSelectedMovement(movement);
    setEditMode(true);
    setMovementDialogOpen(true);
  };

  const handleCloseMovementDialog = () => {
    setMovementDialogOpen(false);
    setSelectedMovement(null);
    setEditMode(false);
  };

  return (
    <Box>
      <TableTitleBox title="Bancos | Movimiento de cuentas" />

      <Box sx={{ width: '100%', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
            backgroundColor: 'background.paper',
          }}
        >
          {selectedAccount && (
            <Box sx={{ padding: 2}}>
              <Typography variant="h6" color="primary">
                {formatBankName(selectedAccount.bank_name)} - {selectedAccount.account_number}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto', p: 2 }}>
            <CustomButton
              variant="base"
              startIcon={<AddIcon />}
              onClick={handleCreateMovement}
              disabled={!selectedAccount}
              size={isMobile ? 'small' : 'medium'}
            >
              Ingresar movimiento
            </CustomButton>
          </Box>
        </Box>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            paddingTop: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
          >
            <Tab label="Resumen de cuentas" />
            <Tab
              label={selectedAccount ? 'Movimientos en cuenta' : 'Seleccionar cuenta'}
              disabled={!selectedAccount}
            />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {activeTab === 0 && (
            <AccountsOverview
              accounts={bankAccounts}
              isLoading={accountsLoading}
              onAccountSelect={handleAccountSelect}
            />
          )}

          {activeTab === 1 && selectedAccount && (
            <MovementsList account={selectedAccount} onEditMovement={handleEditMovement} />
          )}
        </Box>

        <MovementDialog
          open={movementDialogOpen}
          onClose={handleCloseMovementDialog}
          movement={selectedMovement}
          account={selectedAccount}
          editMode={editMode}
        />
      </Box>
    </Box>
  );
};

export default BankMovements;
